import { Injectable, inject } from '@angular/core'
import { ProductType } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { pick } from 'lodash-es'
import { AuthApiService } from 'src/api/auth'
import { ProductTypeApiService } from 'src/api/product/type'

const ALLOWED_FIELDS: Array<keyof ProductType> = ['name', 'category_id', 'attributes']
const CACHE_MAX_AGE = 3600 * 1000 // 1hr


/**
 * ProductTypes service
 *
 * @export
 * @returns
 */
@Injectable()
export class ProductTypesService {
  private AuthApi = inject(AuthApiService)
  private ProductTypeApi = inject(ProductTypeApiService)



  private cacheExpiration = 0
  private cache = Promise.resolve({ data: [] })

  /**
   * Get all available product-types
   *
   * @param {any} query
   * @returns {Object} { total_rows:number, data: []}
   */
  async getProductTypes() {
    if (!this.cache || this.cacheExpiration < Date.now()) {
      this.cacheExpiration = Date.now() + CACHE_MAX_AGE // 1 min
      // NOTE: we fetch everything
      this.cache = this.ProductTypeApi.list({ limit: Number.MAX_SAFE_INTEGER })
      .then(async (productTypes) => {
        productTypes.data.forEach(productType => {
          productType.created = productType.created || productType['createdAt']
          productType.updated = productType['updatedAt'] || productType.updated || productType.created
        })
        return productTypes
      })
      .catch((err) => {
        this.invalidateCache()
        throw err
      })
    }
    const { data } = await this.cache
    return data
  }

  /**
   * Create new productType
   *
   * @param {any} productType
   * @returns
   */
  async create(productType: DeepReadonly<Partial<ProductType>>) {
    const payload = pick(productType, ALLOWED_FIELDS)
    const { user_id } = this.AuthApi.currentUser
    const { data } = await this.ProductTypeApi.create({...payload, user_id})
    this.invalidateCache()
    return data
  }

  /**
   * Update productType
   *
   * @param {any} productType
   * @returns
   */
  async update(productType: DeepReadonly<Partial<ProductType>>) {
    const { type_id } = productType
    const payload = pick(productType, ALLOWED_FIELDS)
    const { user_id } = this.AuthApi.currentUser

    const { data } = await this.ProductTypeApi.update(type_id, {...payload, user_id})
    this.invalidateCache()
    return data
  }

  /**
   * Invalidate product-types in-memory cache
   *
   * @private
   */
  private invalidateCache() {
    this.cacheExpiration = 0
  }
}
